import type {
  GetApiV1ThreeLetterCodePartnerAdminsBankAccounts200One,
  GetApiV1ThreeLetterCodePartnerAdminsUsers200,
} from '@fukuri-admins/apis/client/gen/model';
import { useNavigate } from '@fukuri-admins/hooks/common';
import { useAlertDialog } from '@fukuri-admins/ui/common/AlertDialog';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { downloadFile } from '@fukuri-admins/utils/common';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useDeletePartnerAdminsUser,
  useGetPartnerAdminsBankAccounts,
  useGetPartnerAdminsUsers,
} from '~/hooks';
import type { UserListBankAccountPresentation } from '../presentations';
import { useGetBankAccountsCsv } from './useGetBankAccountsCsv';

type PresentationProps = React.ComponentProps<typeof UserListBankAccountPresentation>;

/** 検索フォームで管理する URL クエリキー */
type FilterKey = 'full_name' | 'email' | 'client_name';

type IUseUserListBankAccount = () => {
  state: {
    /** API から取得した利用者一覧データ */
    usersData: GetApiV1ThreeLetterCodePartnerAdminsUsers200;
    /** API から取得した口座情報一覧データ（利用者一覧取得後の依存フェッチ） */
    bankAccountsData: GetApiV1ThreeLetterCodePartnerAdminsBankAccounts200One | undefined;
    /** 総ページ数 */
    totalPage: number;
    /** 総件数 */
    totalCount: number;
    /** 現在のページ番号（1始まり） */
    currentPage: number;
    /** 名前検索フォームの値（filterParams からの draft 値） */
    fullNameValue: string;
    /** メールアドレス検索フォームの値（filterParams からの draft 値） */
    emailValue: string;
    /** 会社名検索フォームの値（filterParams からの draft 値） */
    clientNameValue: string;
    /**
     * 検索条件が適用されているか（committedFilterParams に検索キーが存在するか）
     *
     * true のとき 0 件でも Empty 画面を表示しない（PEN-309 対応パターン）
     */
    isFiltered: boolean;
  };
  actions: {
    /** ページ変更時のハンドラ */
    handlePageChange: (page: number) => void;
    /** 「編集」押下時のハンドラ */
    handleEditUser: PresentationProps['onEditUser'];
    /** 「削除」押下時のハンドラ */
    handleDeleteUser: PresentationProps['onDeleteUser'];
    /** 行クリック時のハンドラ */
    handleRowClick: PresentationProps['onRowClick'];
    /** 検索フォーム変更時の共通ハンドラ（key に応じた URL クエリを draft 更新） */
    handleFilterChange: (key: FilterKey) => (value: string) => void;
    /** 検索ボタン押下時のハンドラ */
    handleSearch: PresentationProps['onSearch'];
    /** 条件リセットボタン押下時のハンドラ */
    handleResetFilters: PresentationProps['onReset'];
    /** CSV ダウンロードボタン押下時のハンドラ */
    handleDownloadCsv: () => void;
    /** CSV ダウンロード中フラグ */
    isDownloadingCsv: boolean;
  };
};

/**
 * 利用者一覧（BankAccount）の状態管理とアクションを提供するコンポーネントフック
 *
 * - URL クエリパラメータ（full_name / email / client_name / page）で
 *   検索・ページング状態を管理する
 * - filterParams（useSearchParams）: 入力途中の draft 値。replace:true で URL に書くが API は発火しない
 * - committedFilterParams（useState）: 検索ボタン押下で確定した値。SWR キーとして API を発火する
 */
export const useUserListBankAccount: IUseUserListBankAccount = () => {
  const [filterParams, setFilterParams] = useSearchParams();
  const { showAlertDialog } = useAlertDialog();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // committedFilterParams（useState）: useGetPartnerAdminsUsers に渡す API 用パラメータ。
  // 検索ボタン押下・クリア・ページ変更時のみ更新することで「検索ボタン押下時のみ API 発火」を実現する。
  const [committedFilterParams, setCommittedFilterParams] = useState(
    () => new URLSearchParams(filterParams)
  );

  const threeLetterCode = getThreeLetterCode() ?? '';

  // URL クエリパラメータから検索フォームの draft 値を導出する（filterParams から読む）
  const fullNameValue = filterParams.get('full_name') || '';
  const emailValue = filterParams.get('email') || '';
  const clientNameValue = filterParams.get('client_name') || '';

  const {
    data: usersData,
    totalPage,
    totalCount,
    mutate,
  } = useGetPartnerAdminsUsers({
    threeLetterCode,
    filter: {
      fullName: committedFilterParams.get('full_name') || undefined,
      email: committedFilterParams.get('email') || undefined,
      clientName: committedFilterParams.get('client_name') || undefined,
    },
    page: committedFilterParams.get('page') || undefined,
  });

  // 利用者一覧取得後に user_id 一覧を取り出して口座情報を依存フェッチする
  const userIds = usersData.users.map(user => user.id);
  const { data: bankAccountsData } = useGetPartnerAdminsBankAccounts({
    threeLetterCode,
    userIds,
  });

  const { trigger: deleteUser } = useDeletePartnerAdminsUser(threeLetterCode, {
    onSuccess: () => {
      showToast({ message: '利用者を削除しました。', status: 'success' });
      mutate();
    },
    onError: () => {
      showToast({ message: '削除に失敗しました。', status: 'danger' });
    },
  });

  // 現在一覧に表示中の利用者 ID を user_ids[] として渡すことで、
  // 検索条件（full_name / email / client_name）を反映した CSV を生成する。
  // 検索未適用時（全件表示時）は userIds を渡さず全件 CSV をダウンロードする。
  const currentUserIds = (usersData.users ?? []).map(u => u.id);
  const hasFilter =
    committedFilterParams.has('full_name') ||
    committedFilterParams.has('email') ||
    committedFilterParams.has('client_name');
  const csvUserIds = hasFilter ? currentUserIds : undefined;

  const { trigger: downloadBankAccountsCsv, isMutating: isDownloadingCsv } = useGetBankAccountsCsv(
    threeLetterCode,
    csvUserIds,
    {
      onSuccess: blob => downloadFile(blob, 'bank_accounts.csv'),
      onError: () => {
        showToast({ message: 'CSVのダウンロードに失敗しました。', status: 'danger' });
      },
    }
  );

  const currentPage = Number(committedFilterParams.get('page')) || 1;

  /**
   * 検索フォーム変更時の共通ハンドラ
   *
   * URL の draft パラメータを replace:true で更新する（API は発火させない）
   * 各フィールドの onChange に `handleFilterChange('full_name')` のようにキーを渡して使う
   *
   * @param key URL クエリキー
   */
  const handleFilterChange = (key: FilterKey) => (value: string) => {
    const newParams = new URLSearchParams(filterParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setFilterParams(newParams, { replace: true });
  };

  /**
   * 検索ボタン押下時のハンドラ
   *
   * filterParams を committedFilterParams にコピーして API を発火させる
   * page はリセットする（client 実装と同じ）
   */
  const handleSearch = () => {
    const newParams = new URLSearchParams(filterParams);
    newParams.delete('page');
    setFilterParams(newParams, { replace: true });
    setCommittedFilterParams(newParams);
  };

  /**
   * 条件リセットボタン押下時のハンドラ
   *
   * 検索条件（full_name / email / client_name）と page を
   * すべて削除し、API を再発火する
   */
  const handleResetFilters = () => {
    const newParams = new URLSearchParams(filterParams);
    newParams.delete('full_name');
    newParams.delete('email');
    newParams.delete('client_name');
    newParams.delete('page');
    setFilterParams(newParams, { replace: true });
    setCommittedFilterParams(newParams);
  };

  /**
   * ページ変更時のハンドラ
   *
   * committedFilterParams ベースで page を更新して API を発火させる
   *
   * @param page 遷移先のページ番号（1始まり）
   */
  const handlePageChange = (page: number) => {
    const newCommitted = new URLSearchParams(committedFilterParams);
    newCommitted.set('page', String(page));
    const newParams = new URLSearchParams(filterParams);
    newParams.set('page', String(page));
    setFilterParams(newParams);
    setCommittedFilterParams(newCommitted);
  };

  /**
   * CSV ダウンロードボタン押下時のハンドラ
   *
   * 検索条件が適用されている場合は現在表示中の利用者 ID を user_ids[] として渡し、
   * 条件を反映した CSV をダウンロードする。
   * 検索未適用時は全件 CSV をダウンロードする。
   */
  const handleDownloadCsv = () => {
    downloadBankAccountsCsv();
  };

  /**
   * 行クリック時のハンドラ
   *
   * 利用者詳細画面へ遷移する
   *
   * @param userId 操作対象の利用者 ID
   */
  const handleRowClick: PresentationProps['onRowClick'] = userId => {
    navigate(`/users/${userId}`);
  };

  /**
   * 「編集」押下時のハンドラ
   *
   * 利用者編集ページへ遷移する
   *
   * @param userId 操作対象の利用者 ID
   */
  const handleEditUser: PresentationProps['onEditUser'] = userId => {
    navigate(`/users/${userId}/edit`);
  };

  /**
   * 「削除」押下時のハンドラ
   *
   * AlertDialog で確認後に削除 API を呼び出す
   *
   * @param userId 操作対象の利用者 ID
   */
  const handleDeleteUser: PresentationProps['onDeleteUser'] = userId => {
    const user = usersData.users.find(u => u.id === userId);
    if (!user) return;

    showAlertDialog({
      title: `${user.full_name}を削除しますか？`,
      message: '削除すると元に戻すことはできません。',
      confirmText: '削除',
      confirmButtonVariant: 'destructive',
      onConfirm: async () => {
        await deleteUser(userId);
      },
    });
  };

  // committedFilterParams に検索キー（page 除く）が1つでも存在すれば isFiltered=true
  const isFiltered =
    committedFilterParams.has('full_name') ||
    committedFilterParams.has('email') ||
    committedFilterParams.has('client_name');

  return {
    state: {
      usersData,
      bankAccountsData,
      totalPage,
      totalCount,
      currentPage,
      fullNameValue,
      emailValue,
      clientNameValue,
      isFiltered,
    },
    actions: {
      handlePageChange,
      handleRowClick,
      handleEditUser,
      handleDeleteUser,
      handleFilterChange,
      handleSearch,
      handleResetFilters,
      handleDownloadCsv,
      isDownloadingCsv,
    },
  };
};
